import React, { useRef, useState, useEffect } from 'react';
import { setPreventUnload } from 'flow/actions/app-actions';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';

interface Props {
    url: string;
    duration?: number;
    downloadUrl?: string;
    presentDownload?: boolean;
    onClickDownload?: () => void;
    onClickPlayPause?: (value: 'play' | 'pause') => void;
    preload?: 'metadata' | 'none' | 'auto';
}

const AudioPlayer = ({
    url,
    duration,
    downloadUrl,
    presentDownload,
    onClickDownload,
    onClickPlayPause,
    preload = 'metadata',
}: Props) => {
    const { i18next } = useAppContext();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const progressBarRef = useRef<HTMLDivElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [timeLeft, setTimeLeft] = useState<number | null>(duration || null);

    useEffect(() => {
        setTimeLeft(duration);
    }, [duration]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;

        const updateProgress = () => {
            if (Number.isNaN(audio.currentTime) || Number.isNaN(audio.duration))
                return;

            setProgress((audio.currentTime / audio.duration) * 100 || 0);
            setTimeLeft(audio.duration - audio.currentTime);
        };

        const onLoadedMetadata = () => {
            setTimeLeft(audio.duration || 0);
        };

        const handleAudioEnded = () => {
            setIsPlaying(false);
            setProgress(0);
            setTimeLeft(audio.duration);
            audio.currentTime = 0;
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', handleAudioEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', handleAudioEnded);
        };
    }, [url]);

    const onDownloadHrefClicked = () => {
        setPreventUnload(true);
        onClickDownload?.();
    };

    const togglePlayPause = () => {
        onClickPlayPause?.(isPlaying ? 'pause' : 'play');
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleProgressClick = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        const audio = audioRef.current;
        const progressBar = progressBarRef.current;

        if (audio && progressBar) {
            const rect = progressBar.getBoundingClientRect();
            const clickX = e.clientX - rect.left; // X position relative to the progress bar
            const newProgress = clickX / rect.width; // Calculate progress as a fraction
            audio.currentTime = newProgress * audio.duration; // Update audio time
            setProgress(newProgress * 100); // Update progress bar visually
            setTimeLeft(audio.duration - audio.currentTime || 0);
        }
    };

    const formatTime = (time?: number) => {
        if (!time) return '';
        const minutes = Math.floor(time / 60)
            .toString()
            .padStart(2, '0');
        const seconds = Math.floor(time % 60)
            .toString()
            .padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    return (
        <div className="flex h-8 grow flex-row items-center gap-1" dir="ltr">
            <IconButton
                onClick={togglePlayPause}
                tooltipLabel={
                    isPlaying
                        ? undefined
                        : i18next.t(i18next.t('Tooltip.Audio.Play'))
                }
                className="rounded-sm bg-gray-200"
            >
                <Icon
                    iconKey={isPlaying ? 'pause' : 'play_arrow'}
                    className="text-gray-600"
                />
            </IconButton>
            <div className="flex h-full w-full items-center gap-2 rounded-sm bg-gray-200 p-2">
                <div
                    className="relative h-1 w-full cursor-pointer rounded-sm bg-gray-300"
                    ref={progressBarRef}
                    onClick={handleProgressClick}
                >
                    <div
                        className="bg-main absolute start-0 top-0 h-1 rounded-sm"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="min-w-10 shrink-0 text-center text-xs text-gray-500 tabular-nums">
                    {formatTime(timeLeft)}
                </div>
            </div>
            {presentDownload ? (
                <a
                    href={downloadUrl || url}
                    download={true}
                    onClick={onDownloadHrefClicked}
                >
                    <IconButton
                        tooltipLabel={i18next.t('Tooltip.Audio.Download')}
                        className="bg-gray-200"
                    >
                        <Icon className="cursor-pointer" iconKey="get_app" />
                    </IconButton>
                </a>
            ) : null}
            <audio ref={audioRef} src={url} preload={preload} />
        </div>
    );
};

export default AudioPlayer;
